import type { Config, PopoverDOM, State } from "driver.js";

const MIN_TARGET_GAP_PX = 2;
const ARROW_INSET_PX = 18;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function buildProgressDots(progressEl: HTMLElement, current: number, total: number) {
  progressEl.textContent = "";
  progressEl.className = "swivu-guide-footer-progress driver-popover-progress-text";

  const dots = document.createElement("span");
  dots.className = "swivu-guide-progress";
  dots.setAttribute("aria-hidden", "true");

  for (let i = 0; i < total; i += 1) {
    const dot = document.createElement("span");
    dot.className = "swivu-guide-progress-dot";
    if (i < current) dot.classList.add("is-complete");
    if (i === current - 1) dot.classList.add("is-active");
    dots.appendChild(dot);
  }

  progressEl.append(dots);
  progressEl.setAttribute("aria-label", `Step ${current} of ${total}`);
}

function findTargetIcon(activeElement: Element | undefined): SVGElement | null {
  if (!activeElement) return null;

  const svgs = activeElement.querySelectorAll("svg");
  for (const svg of Array.from(svgs)) {
    const { width, height } = svg.getBoundingClientRect();
    if (width >= 10 && height >= 10) return svg;
  }

  return svgs[0] ?? null;
}

function ensureGuideHeading(titleEl: HTMLElement) {
  if (titleEl.parentElement?.classList.contains("swivu-guide-heading")) return;

  const heading = document.createElement("div");
  heading.className = "swivu-guide-heading";
  const iconSlot = document.createElement("span");
  iconSlot.className = "swivu-guide-title-icon";
  iconSlot.setAttribute("aria-hidden", "true");
  titleEl.replaceWith(heading);
  heading.append(iconSlot, titleEl);
}

function syncTitleIcon(titleEl: HTMLElement, activeElement: Element | undefined) {
  ensureGuideHeading(titleEl);

  const iconSlot = titleEl.parentElement?.querySelector<HTMLElement>(".swivu-guide-title-icon");
  if (!iconSlot) return;

  iconSlot.replaceChildren();
  const sourceSvg = findTargetIcon(activeElement);
  if (!sourceSvg) {
    iconSlot.hidden = true;
    return;
  }

  iconSlot.hidden = false;
  const clone = sourceSvg.cloneNode(true) as SVGElement;
  clone.removeAttribute("id");
  iconSlot.appendChild(clone);
}

function ensureGuideStructure(popover: PopoverDOM, activeElement: Element | undefined) {
  popover.wrapper.classList.add("swivu-page-guide-popover");
  popover.footer.classList.add("swivu-guide-footer");
  popover.closeButton.classList.add("swivu-guide-close");
  popover.closeButton.textContent = "Skip";
  popover.closeButton.setAttribute("aria-label", "Skip tour");
  popover.arrow.classList.add("swivu-guide-arrow");

  let body = popover.wrapper.querySelector<HTMLElement>(".swivu-guide-body");
  if (!body) {
    body = document.createElement("div");
    body.className = "swivu-guide-body";
    body.append(popover.title, popover.description);
    popover.wrapper.insertBefore(body, popover.footer);
  }

  syncTitleIcon(popover.title, activeElement);
}

function resetArrowAlignment(arrow: HTMLElement) {
  arrow.classList.remove(
    "driver-popover-arrow-align-start",
    "driver-popover-arrow-align-center",
    "driver-popover-arrow-align-end",
  );
}

function alignArrowToTarget(popover: PopoverDOM, activeElement: Element | undefined) {
  if (!activeElement || !popover.arrow) return;

  const targetRect = activeElement.getBoundingClientRect();
  const popoverRect = popover.wrapper.getBoundingClientRect();
  const arrow = popover.arrow;

  resetArrowAlignment(arrow);

  if (
    arrow.classList.contains("driver-popover-arrow-side-bottom") ||
    arrow.classList.contains("driver-popover-arrow-side-top")
  ) {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const offsetX = clamp(targetCenterX - popoverRect.left, ARROW_INSET_PX, popoverRect.width - ARROW_INSET_PX);
    arrow.style.left = `${offsetX}px`;
    arrow.style.right = "auto";
    arrow.style.transform = "translateX(-50%)";
    return;
  }

  if (
    arrow.classList.contains("driver-popover-arrow-side-left") ||
    arrow.classList.contains("driver-popover-arrow-side-right")
  ) {
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const offsetY = clamp(targetCenterY - popoverRect.top, ARROW_INSET_PX, popoverRect.height - ARROW_INSET_PX);
    arrow.style.top = `${offsetY}px`;
    arrow.style.bottom = "auto";
    arrow.style.transform = "translateY(-50%)";
  }
}

function nudgePopoverTowardTarget(popover: PopoverDOM, activeElement: Element | undefined) {
  if (!activeElement) return;

  const targetRect = activeElement.getBoundingClientRect();
  const popRect = popover.wrapper.getBoundingClientRect();
  const arrow = popover.arrow;

  if (arrow.classList.contains("driver-popover-arrow-side-bottom")) {
    const gap = popRect.top - targetRect.bottom;
    if (gap > MIN_TARGET_GAP_PX) {
      popover.wrapper.style.top = `${popRect.top - (gap - MIN_TARGET_GAP_PX)}px`;
    }
    return;
  }

  if (arrow.classList.contains("driver-popover-arrow-side-top")) {
    const gap = targetRect.top - popRect.bottom;
    if (gap > MIN_TARGET_GAP_PX) {
      popover.wrapper.style.top = `${popRect.top + (gap - MIN_TARGET_GAP_PX)}px`;
    }
    return;
  }

  if (arrow.classList.contains("driver-popover-arrow-side-left")) {
    const gap = popRect.left - targetRect.right;
    if (gap > MIN_TARGET_GAP_PX) {
      popover.wrapper.style.left = `${popRect.left - (gap - MIN_TARGET_GAP_PX)}px`;
    }
    return;
  }

  if (arrow.classList.contains("driver-popover-arrow-side-right")) {
    const gap = targetRect.left - popRect.right;
    if (gap > MIN_TARGET_GAP_PX) {
      popover.wrapper.style.left = `${popRect.left + (gap - MIN_TARGET_GAP_PX)}px`;
    }
  }
}

function syncNavigationButtons(popover: PopoverDOM, currentIndex: number, total: number) {
  const isFirst = currentIndex === 0;
  const isLast = currentIndex >= total - 1;

  popover.previousButton.classList.toggle("swivu-guide-nav-hidden", isFirst);
  popover.previousButton.toggleAttribute("hidden", isFirst);

  if (isLast) {
    popover.nextButton.textContent = "Got it";
    popover.nextButton.classList.add("driver-popover-done-btn");
  } else {
    popover.nextButton.textContent = "Next";
    popover.nextButton.classList.remove("driver-popover-done-btn");
  }
}

export function enhanceGuidePopover(
  popover: PopoverDOM,
  opts: { config: Config; state: State },
) {
  const total = Math.max(1, opts.config.steps?.length ?? 1);
  const currentIndex = opts.state.activeIndex ?? 0;
  const current = currentIndex + 1;
  const activeElement = opts.state.activeElement;

  ensureGuideStructure(popover, activeElement);
  buildProgressDots(popover.progress, current, total);
  syncNavigationButtons(popover, currentIndex, total);

  requestAnimationFrame(() => {
    nudgePopoverTowardTarget(popover, activeElement);
    alignArrowToTarget(popover, activeElement);
  });
}
